import type { Equipment, EquipmentType } from "../../equipment/equipment-domain.js";

/**
 * BOOK DE INSPEÇÃO — TIPO DE CONTEXTO (comando: item 1).
 *
 * O "ITEM" selecionado pelo usuário é sempre um Equipment já cadastrado — um
 * Sistema de Tubulação e uma Tubulação/Linha são, no modelo real do
 * INSPECT IA, o mesmo Equipment com `equipmentType` PIPING_SYSTEM ou
 * PIPING_LINE (ver equipment-domain.ts); "Equipamento Estático" é qualquer
 * outro tipo. Nenhum conceito novo é criado — só se deriva o tipo de
 * contexto do cadastro existente.
 */
export type BookContextType = "SISTEMA_TUBULACAO" | "TUBULACAO_LINHA" | "EQUIPAMENTO_ESTATICO";

export const BOOK_CONTEXT_TYPE_LABELS: Record<BookContextType, string> = {
  SISTEMA_TUBULACAO: "Sistema de Tubulação",
  TUBULACAO_LINHA: "Tubulação / Linha",
  EQUIPAMENTO_ESTATICO: "Equipamento Estático"
};

export function bookContextTypeOf(equipmentType: EquipmentType): BookContextType {
  if (equipmentType === "PIPING_SYSTEM") return "SISTEMA_TUBULACAO";
  if (equipmentType === "PIPING_LINE") return "TUBULACAO_LINHA";
  return "EQUIPAMENTO_ESTATICO";
}

/** As sete seções do Book (comando: item 22), na ordem oficial de consolidação (item 27). */
export type BookSectionId =
  | "RELATORIOS"
  | "MINUTAS"
  | "ISOMETRICOS"
  | "LISTA_DE_LINHAS"
  | "FOLHA_DE_DADOS"
  | "FLUXOGRAMAS"
  | "PITS";

export const BOOK_SECTION_ORDER: readonly BookSectionId[] = [
  "RELATORIOS", "MINUTAS", "ISOMETRICOS", "LISTA_DE_LINHAS", "FOLHA_DE_DADOS", "FLUXOGRAMAS", "PITS"
];

export const BOOK_SECTION_LABELS: Record<BookSectionId, string> = {
  RELATORIOS: "Relatórios",
  MINUTAS: "Minutas",
  ISOMETRICOS: "Isométricos de Inspeção",
  LISTA_DE_LINHAS: "Lista de Linhas",
  FOLHA_DE_DADOS: "Folha de Dados",
  FLUXOGRAMAS: "Fluxogramas",
  PITS: "PITs"
};

/**
 * Aplicabilidade de cada seção por tipo de contexto — exatamente a matriz do
 * comando (itens 8, 9, 12, 37, 38, 39). LISTA_DE_LINHAS só no Sistema (nunca
 * na linha individual — o comando não a lista no grupo "TUBULAÇÃO/LINHA");
 * FOLHA_DE_DADOS só no Equipamento Estático; ISOMÉTRICOS no Sistema e na
 * Linha, não no Equipamento Estático; FLUXOGRAMAS e PITS em todos.
 */
const SECTION_APPLICABILITY: Record<BookContextType, ReadonlySet<BookSectionId>> = {
  SISTEMA_TUBULACAO: new Set(["RELATORIOS", "MINUTAS", "ISOMETRICOS", "LISTA_DE_LINHAS", "FLUXOGRAMAS", "PITS"]),
  TUBULACAO_LINHA: new Set(["RELATORIOS", "MINUTAS", "ISOMETRICOS", "FLUXOGRAMAS", "PITS"]),
  EQUIPAMENTO_ESTATICO: new Set(["RELATORIOS", "MINUTAS", "FOLHA_DE_DADOS", "FLUXOGRAMAS", "PITS"])
};

export function isSectionApplicable(contextType: BookContextType, section: BookSectionId): boolean {
  return SECTION_APPLICABILITY[contextType].has(section);
}

/** Seções aplicáveis ao tipo de contexto, na ordem oficial — o que a página deve renderizar (sem espaço vazio para o resto, item 22). */
export function applicableSectionsFor(contextType: BookContextType): BookSectionId[] {
  return BOOK_SECTION_ORDER.filter(section => isSectionApplicable(contextType, section));
}

export interface ResolvedBookContext {
  clientId: string;
  projectId: string | null;
  unitAreaId: string | null;
  equipmentId: string;
  equipmentTag: string;
  equipmentName: string;
  contextType: BookContextType;
  contextTypeLabel: string;
  /** Sistema de Tubulação pai, quando o item é uma Linha (equipment.pipingSystemId) — só para exibição, nunca herda documentos do Sistema (comando item 39). */
  parentPipingSystemId: string | null;
}

/**
 * FONTE ÚNICA DE CONTEXTO (comando item 14) — pura, sem I/O. Todas as seções
 * do Book usam este mesmo resultado; nenhuma seção resolve o contexto por
 * conta própria.
 */
export function resolveInspectionBookContext(equipment: Pick<Equipment, "id" | "clientId" | "tag" | "name" | "equipmentType" | "projectId" | "unitAreaId" | "pipingSystemId">): ResolvedBookContext {
  const contextType = bookContextTypeOf(equipment.equipmentType);
  return {
    clientId: String(equipment.clientId),
    projectId: equipment.projectId ? String(equipment.projectId) : null,
    unitAreaId: equipment.unitAreaId ? String(equipment.unitAreaId) : null,
    equipmentId: String(equipment.id),
    equipmentTag: equipment.tag || "",
    equipmentName: equipment.name || "",
    contextType,
    contextTypeLabel: BOOK_CONTEXT_TYPE_LABELS[contextType],
    parentPipingSystemId: contextType === "TUBULACAO_LINHA" && equipment.pipingSystemId ? String(equipment.pipingSystemId) : null
  };
}
